import { Callout } from '@dev-spendesk/grapes';
import React from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';

import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';

import { useCompany } from '../../app/hooks/useCompany';
import { isIntegrationStatusWithIntegration } from '../../bookkeep/integration/status';
import { useAccountingIntegrationStatusQuery } from '../apis';
import {
  hasBankAccountsError,
  hasJournalsError,
  haveSettingsAtLeastOneValidationError,
} from '../models';

export const InvalidAccountingIntegrationSettingsFeedback = ({
  className = '',
}: {
  className?: string;
}) => {
  const { t } = useTranslation('global');

  const company = useCompany();

  const integrationStatusQuery = useAccountingIntegrationStatusQuery();

  const integrationStatus = unwrapQuery(integrationStatusQuery);

  // The branch_expense_entity company type does not have access
  // to the settings needed to fix the problems
  // and does not need them since they don't use settlements
  if (company.type === 'branch_expense_entity') {
    return null;
  }

  if (
    integrationStatus &&
    isIntegrationStatusWithIntegration(integrationStatus) &&
    haveSettingsAtLeastOneValidationError(integrationStatus.settingsValidation)
  ) {
    return (
      <Callout
        variant="warning"
        title={t('accounting-integration.invalidSettingsFeedback.title')}
        className={className}
      >
        <ul className="list-inside list-disc">
          {hasBankAccountsError(integrationStatus.settingsValidation) && (
            <li>
              <Trans i18nKey="accounting-integration.invalidSettingsFeedback.bankAccountIssues">
                <Link
                  className="text-content-primary underline"
                  to={{
                    pathname: routeFor(
                      routes.COMPANY_ACCOUNTING_BANK_ACCOUNTS.path,
                      {
                        company: company.id,
                      },
                    ),
                  }}
                >
                  -
                </Link>
              </Trans>
            </li>
          )}
          {hasJournalsError(integrationStatus.settingsValidation) && (
            <li>
              <Trans i18nKey="accounting-integration.invalidSettingsFeedback.journalIssues">
                <Link
                  className="text-content-primary underline"
                  to={{
                    pathname: routeFor(
                      routes.COMPANY_ACCOUNTING_INTEGRATION.path,
                      {
                        company: company.id,
                      },
                    ),
                  }}
                >
                  -
                </Link>
              </Trans>
            </li>
          )}
        </ul>
      </Callout>
    );
  }
};
